import { QueryClient } from '@tanstack/react-query';
import { PokerGraphqlRequestError } from '../../Utils/PokerGraphqlClient';

/**
 * Default QueryClient configuration optimized for the application.
 * - staleTime: Data is stale immediately (always eligible for refetch)
 * - gcTime: Unused data is garbage collected after 10 minutes
 * - retry: Failed requests are retried twice (except auth/session failures)
 * - refetchOnWindowFocus: Disabled to avoid unnecessary refetches
 */
const defaultQueryClientOptions = {
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 10 * 60 * 1000,
      retry: (failureCount: number, error: unknown) => {
        if (error instanceof PokerGraphqlRequestError) {
          const message = error.message.toLowerCase();
          if (
            message.includes('missing session token') ||
            message.includes('unauthorized') ||
            message.includes('forbidden')
          ) {
            return false;
          }
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
};

/**
 * Singleton QueryClient instance for the application.
 * This ensures all queries share the same cache.
 */
export const queryClient = new QueryClient(defaultQueryClientOptions);

export const resetQueryClient = async (): Promise<void> => {
  try {
    await queryClient.cancelQueries();
  } catch {}
  queryClient.clear();
};
